import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { styled } from "@mui/material/styles";
import { VolumeMute, VolumeOff, PlayArrow, Pause } from "@mui/icons-material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { changeMute, playingStatus } from "../../reducers/players/playersSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import certifiedLogo from "../../assets/logo_certified.png";
import {
  isFullscreenModeEnabled,
  isPlayerFullscreenSupported,
  usePlayerFullscreen,
} from "../../helpers";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/fr";
import "dayjs/locale/en";

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const PREFIX = "PlayerFooter";

const classes = {
  horizontalPlayerFooter: `${PREFIX}-horizontalPlayerFooter`,
  verticalPlayerFooter: `${PREFIX}-verticalPlayerFooter`,
  playerFooterText: `${PREFIX}-playerFooterText`,
  authorBlock: `${PREFIX}-authorBlock`,
  authorLine: `${PREFIX}-authorLine`,
  metaColumn: `${PREFIX}-metaColumn`,
  postedAtLine: `${PREFIX}-postedAtLine`,
  miniAvatar: `${PREFIX}-miniAvatar`,
  miniAvatarDefault: `${PREFIX}-miniAvatarDefault`,
  login: `${PREFIX}-login`,
  certifiedBadge: `${PREFIX}-certifiedBadge`,
  loginVertical: `${PREFIX}-loginVertical`,
  description: `${PREFIX}-description`,
  muteIcons: `${PREFIX}-muteIcons`,
  horizontalPlayerButtons: `${PREFIX}-horizontalPlayerButtons`,
  iconBackground: `${PREFIX}-iconBackground`,
};

const Root = styled("div")(({ theme }) => ({
  [`&.${classes.horizontalPlayerFooter}`]: {
    color: "white",
    minHeight: "55px",
    height: "auto",
    display: "flex",
    position: "absolute",
    top: "0px",
    width: "100%",
    paddingBottom: theme.spacing(0.5),
  },
  [`&.${classes.verticalPlayerFooter}`]: {
    color: "white",
    display: "flex",
    height: "90px",
    minHeight: "90px",
    maxHeight: "90px",
    boxSizing: "border-box",
    position: "absolute",
    bottom: "0",
    width: "100%",
    paddingRight: "72px",
    pointerEvents: "none",
    zIndex: 1200,
  },
  [`& .${classes.playerFooterText}`]: {
    flex: "1",
    fontSize: "12px",
    paddingTop: "0.25rem",
    paddingLeft: "12px",
    minWidth: 0,
    "& > h3": {
      marginBottom: "0.5rem",
      marginTop: "0",
    },
    "& > p": {
      margin: "0",
    },
  },
  [`&.${classes.verticalPlayerFooter} .${classes.playerFooterText}`]: {
    paddingBottom: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    overflow: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
  },

  [`& .${classes.authorBlock}`]: {
    margin: 0,
    padding: 0,
    fontWeight: "inherit",
    fontSize: "inherit",
  },

  [`& .${classes.authorLine}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    minWidth: 0,
  },

  [`& .${classes.metaColumn}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    minWidth: 0,
    flex: 1,
  },

  [`& .${classes.postedAtLine}`]: {
    fontSize: "11px",
    lineHeight: 1.35,
    color: "rgba(255, 255, 255, 0.88)",
    maxWidth: "min(240px, 72vw)",
    wordBreak: "break-word",
  },

  [`& .${classes.miniAvatar}`]: {
    borderRadius: "50%",
    height: "45px",
    width: "45px",
    objectFit: "cover",
    marginRight: "0.5rem",
    flexShrink: 0,
  },

  [`& .${classes.miniAvatarDefault}`]: {
    borderRadius: "50%",
    height: "45px",
    width: "45px",
    objectFit: "cover",
    marginRight: "0.5rem",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  [`& .${classes.login}`]: {
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.2,
    wordBreak: "break-word",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },

  [`& .${classes.certifiedBadge}`]: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },

  [`& .${classes.description}`]: {
    marginTop: "-0.5rem",
  },

  [`& .${classes.iconsColor}`]: {
    color: theme.palette.primary.main,
  },
  [`& .${classes.horizontalPlayerButtons}`]: {
    display: "flex",
    justifyContent: "space-between",
    width: "70px",
    position: "absolute",
    top: "15px",
    right: "20px",
    zIndex: "1200",
  },
  [`& .${classes.iconBackground}`]: {
    backgroundColor: "rgba(85, 85, 85, 0.3)",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
  },
}));

function formatPublicationLabel(createdDate, t, lang) {
  if (!createdDate) return "";
  const created = dayjs(createdDate);
  if (!created.isValid()) return "";

  const locale = lang === "fr" ? "fr" : "en";
  dayjs.locale(locale);

  const diffDays = dayjs().diff(created, "day");
  if (diffDays <= 31) {
    return created.fromNow();
  }
  return t("player.publishedAt", {
    date: created.locale(locale).format("LL"),
  });
}

const PlayerFooter = ({ mutable, verticalPlayer, isModelViewer, videoTag }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentElement = useSelector(
    (state) => state.players.playlist[state.players.currentPlayingIndex]
  );

  const muted = useSelector((state) => state.players.muted);
  const isPlaying = useSelector((state) => state.players.playing);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [showCustomMessageMore, setShowCustomMessageMore] = useState(false);
  const customMessageMeasureRef = useRef(null);
  const customMessageContainerRef = useRef(null);

  const lang = (i18n.language || "en").substring(0, 2);
  const postedAt =
    currentElement.pushedBy?.login &&
    formatPublicationLabel(currentElement.createdDate, t, lang);

  const customMessage =
    currentElement.type !== "message" &&
    currentElement.message &&
    String(currentElement.message).trim() !== ""
      ? String(currentElement.message).trim()
      : "";

  useEffect(() => {
    setMessageDialogOpen(false);
  }, [currentElement.id]);

  const updateCustomMessageTruncation = useCallback(() => {
    const el = customMessageMeasureRef.current;
    if (!el || !customMessage) {
      setShowCustomMessageMore(false);
      return;
    }
    setShowCustomMessageMore(el.scrollHeight - el.clientHeight > 1);
  }, [customMessage]);

  useLayoutEffect(() => {
    updateCustomMessageTruncation();
  }, [updateCustomMessageTruncation, verticalPlayer]);

  useEffect(() => {
    const el = customMessageContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => updateCustomMessageTruncation());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateCustomMessageTruncation]);

  const handlePlayingStatus = (play = false) => {
    if (!play) {
      dispatch(playingStatus(false));
      if (videoTag) videoTag.pause();
    } else {
      dispatch(playingStatus(true));
      if (videoTag) videoTag.play();
    }
  };

  const showMuteButton = verticalPlayer && mutable;
  const showFullscreenButton =
    isFullscreenModeEnabled() &&
    isPlayerFullscreenSupported({ videoEl: videoTag });
  const { isFullscreenUi, toggleFullscreen } = usePlayerFullscreen(videoTag);
  const bottomControlCount =
    (showMuteButton ? 1 : 0) + (showFullscreenButton ? 1 : 0);
  const verticalFooterPaddingRight =
    bottomControlCount >= 2 ? "136px" : bottomControlCount === 1 ? "72px" : null;

  return (
    <Root
      className={
        !verticalPlayer
          ? classes.horizontalPlayerFooter
          : classes.verticalPlayerFooter
      }
      style={{
        backgroundColor: isModelViewer ? "inherit" : "rgba(85, 85, 85, 0.3)",
        ...(verticalFooterPaddingRight
          ? { paddingRight: verticalFooterPaddingRight }
          : {}),
      }}
    >
      <div className={classes.playerFooterText}>
        {currentElement.pushedBy?.login && (
          <h3
            className={classes.authorBlock}
            style={
              verticalPlayer ? { marginBottom: "0.2rem" } : undefined
            }
          >
            <div className={classes.authorLine}>
              {!currentElement.pushedBy?.avatar ? (
                <AccountCircleIcon className={classes.miniAvatarDefault} />
              ) : (
                <img
                  src={currentElement.pushedBy?.avatar}
                  alt={`avatar ${currentElement.pushedBy?.login}`}
                  className={classes.miniAvatar}
                />
              )}
              <div className={classes.metaColumn}>
                <span className={classes.login}>
                  {currentElement.pushedBy?.login}
                  {currentElement.pushedBy?.isCertified !== false &&
                    currentElement.pushedBy?.isCertified !== 0 && (
                      <img
                        src={certifiedLogo}
                        alt={t("player.certified")}
                        className={classes.certifiedBadge}
                      />
                    )}
                </span>
                {postedAt ? (
                  <span className={classes.postedAtLine}>{postedAt}</span>
                ) : null}
              </div>
            </div>
          </h3>
        )}
        {!verticalPlayer && (
          <div
            className={classes.horizontalPlayerButtons}
            style={showFullscreenButton ? { width: "105px" } : undefined}
          >
            {isPlaying ? (
              <Pause
                className={classes.iconBackground}
                color="primary"
                onClick={() => handlePlayingStatus(false)}
              />
            ) : (
              <PlayArrow
                className={classes.iconBackground}
                color="primary"
                onClick={() => handlePlayingStatus(true)}
              />
            )}
            {mutable && muted ? (
              <VolumeOff
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(changeMute());
                }}
                className={classes.iconBackground}
              />
            ) : mutable && !muted ? (
              <VolumeMute
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(changeMute());
                }}
                className={classes.iconBackground}
              />
            ) : null}
            {showFullscreenButton && (
              isFullscreenUi ? (
                <CloseFullscreenIcon
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                  className={classes.iconBackground}
                />
              ) : (
                <OpenInFullIcon
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                  className={classes.iconBackground}
                />
              )
            )}
          </div>
        )}
        {customMessage && (
          <>
            <Box
              ref={customMessageContainerRef}
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: verticalPlayer ? "96%" : "92%",
                minWidth: 0,
                mt: verticalPlayer ? 0.125 : 0.5,
                pointerEvents: "auto",
              }}
            >
              <Box
                ref={customMessageMeasureRef}
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  visibility: "hidden",
                  pointerEvents: "none",
                  fontSize: "0.75rem",
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: verticalPlayer ? 2 : 3,
                  overflow: "hidden",
                  m: 0,
                  p: 0,
                }}
              >
                {customMessage}
              </Box>
              <Typography
                variant="caption"
                component="p"
                title={customMessage}
                sx={{
                  m: 0,
                  p: 0,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: verticalPlayer ? 2 : 3,
                  overflow: "hidden",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                  opacity: 0.92,
                  color: "rgba(255, 255, 255, 0.92)",
                }}
              >
                {customMessage}
                {showCustomMessageMore && (
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    {"\u202f"}
                    <Link
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMessageDialogOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setMessageDialogOpen(true);
                        }
                      }}
                      sx={{
                        fontWeight: 700,
                        fontSize: "inherit",
                        color: "primary.main",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {t("player.customMessageMore")}
                    </Link>
                  </Box>
                )}
              </Typography>
            </Box>
            <Dialog
              open={messageDialogOpen}
              onClose={() => setMessageDialogOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>{t("player.customMessageTitle")}</DialogTitle>
              <DialogContent dividers>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {customMessage}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setMessageDialogOpen(false)}
                  color="primary"
                  variant="contained"
                >
                  {t("player.dialogClose")}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </Root>
  );
};

export default PlayerFooter;
